import React from 'react'

import {
  Text,
  HStack,
  Stack,
  Checkbox,
  Card,
  Link,
  Button,
} from '@chakra-ui/react'
import { ExternalLink } from 'src/components/icon'
import { InformationMessage } from 'src/components/InformationMessage/InformationMessage'
import { OptionalBadge } from 'src/components/OptionalBadge/OptionalBadge'
import type { components } from 'src/lib/api-client/schema.gen'

import {
  IcfDocumentType,
  icfDocumentTypes,
  icfDocumentTypeText,
  IcfDocument,
  IcfDocumentSignerRole,
  isDocuSignType,
  IcfDocumentCheckRole,
  SignField,
  icfDocumentSignFieldTypes,
  icfDocumentSignFieldTypeText,
  icfDocumentSignFieldTypeToRole,
} from '../entity'
import { IcfDocumentErrors } from '../validate'
import { IcfDocumentRevisionStatusBadge } from './icf-document-status-badge'

type ReadOnlyProps = {
  name: string
  documentType: IcfDocumentType
  fileName: string
  status: components['schemas']['output.IcfRevStatus']
  version: string
  isRequiredPatientSign: boolean
  filePreviewUrl: string
  signFields?: SignField[]
  errors?: IcfDocumentErrors
  hasTempFile: boolean
  numberingId: IcfDocument['numberingId']
  onRedirectDocuSignPreview: (
    role: IcfDocumentSignerRole | IcfDocumentCheckRole,
  ) => void
  hasMultipleRevisionHistories: boolean
  openRevisionHistory: () => void
}

export const IcfDocumentCardReadOnlyRenewal: React.FC<
  ReadOnlyProps
> = props => {
  return (
    <Card
      as="section"
      pos="relative"
      w="full"
      p="6"
      aria-label="read-only-document-card"
    >
      <Stack spacing="8">
        <IcfDocumentRevisionStatusBadge status={props.status} />

        <HStack spacing="10">
          {props.numberingId !== undefined && (
            <Stack spacing="2">
              <Text fontWeight="medium" fontSize="sm" color="gray.600">
                文書ID
              </Text>
              <Text
                color={props.status === 'Disabled' ? 'gray.400' : undefined}
              >
                {props.numberingId}
              </Text>
            </Stack>
          )}

          {/* 版数 */}
          <Stack>
            <Text fontWeight="medium" fontSize="sm" color="gray.600">
              版数
            </Text>
            <HStack spacing="4">
              <Stack>
                <Text
                  color={props.status === 'Disabled' ? 'gray.400' : undefined}
                >
                  {props.version}
                </Text>
              </Stack>
              {props.hasMultipleRevisionHistories && (
                <Button variant="text" onClick={props.openRevisionHistory}>
                  改版履歴
                </Button>
              )}
            </HStack>
          </Stack>
        </HStack>

        {/* 文書名 */}
        <Stack spacing="2">
          <Text fontSize="sm" fontWeight="medium" color="gray.600">
            文書名
          </Text>
          <Text color={props.status === 'Disabled' ? 'gray.400' : undefined}>
            {props.name}
          </Text>
        </Stack>

        {/* 文書の種類 */}
        <Stack spacing="2">
          <Text fontWeight="medium" fontSize="sm" color="gray.600">
            文書の種類
          </Text>
          <Text color={props.status === 'Disabled' ? 'gray.400' : undefined}>
            {icfDocumentTypeText[props.documentType]}
          </Text>
          {props.documentType === icfDocumentTypes.AgreementForm && (
            <Checkbox
              isChecked={!!props.isRequiredPatientSign}
              isDisabled={true}
            >
              患者の同意必須
            </Checkbox>
          )}
        </Stack>

        {/* ファイル */}
        {!!props.documentType && (
          <Stack spacing="2">
            <Text fontWeight="medium" fontSize="sm" color="gray.600">
              ファイル名
            </Text>
            <HStack spacing="4" align="center">
              <HStack align="center" spacing="2">
                <Link
                  href={props.filePreviewUrl}
                  isExternal
                  color="blue.500"
                  rel="noopener noreferrer"
                >
                  <HStack as="span">
                    <span>{props.fileName}</span>
                    <ExternalLink />
                  </HStack>
                </Link>
              </HStack>
            </HStack>
          </Stack>
        )}

        {!!props.documentType &&
          isDocuSignType(props.documentType) &&
          props.fileName &&
          props.documentType === icfDocumentTypes.AgreementForm &&
          !!props.signFields && (
            <>
              <Stack spacing="2">
                <Text fontWeight="medium" fontSize="sm" color="gray.600">
                  署名ロール
                </Text>
                <HStack align="center" spacing="6">
                  {props.documentType === icfDocumentTypes.AgreementForm &&
                    !!props.signFields && (
                      <HStack align="center">
                        {icfDocumentSignFieldTypes.map(signerType => (
                          <HStack key={signerType} spacing={1} marginRight={4}>
                            <Button
                              key={signerType}
                              size="sm"
                              variant="link"
                              fontWeight="bold"
                              onClick={() =>
                                props.onRedirectDocuSignPreview(
                                  icfDocumentSignFieldTypeToRole[signerType],
                                )
                              }
                              isDisabled={
                                !props.signFields?.some(
                                  signField =>
                                    signField.signFieldType === signerType,
                                )
                              }
                              _disabled={{
                                color: 'gray.400',
                                pointerEvents: 'none',
                              }}
                            >
                              {icfDocumentSignFieldTypeText[signerType]}
                            </Button>
                            {props.signFields?.find(
                              signField =>
                                signField.signFieldType === signerType,
                            )?.optional && <OptionalBadge />}
                          </HStack>
                        ))}
                      </HStack>
                    )}
                </HStack>
                <InformationMessage message="※ロール名を押下してDocuSignの設定が確認できます。" />
              </Stack>
            </>
          )}
      </Stack>
    </Card>
  )
}
